/* eslint-disable sort-keys-fix/sort-keys-fix */
// The order of routes matter
import { Dispatch } from 'redux'
import { Nullable, RoutesMap } from 'redux-first-router'

import { Flag } from '~shared-components/components/FlagRender'
import { NavigationSelector } from '~shared-components/redux/selectors/navigation.selector'
import { SharedState } from '~shared-components/redux/sharedState'
import { NavigationBlockModalType, Page } from '~shared-components/utils/enums'
import {
  redirectToNotFound,
  redirectToPage,
} from '~shared-components/utils/router'

const BASE_PATH = '/:language?-:country?/connect'

/**
 *
 * You can block navigation and display a modal by adding:

confirmLeave: (state: object) => {
    return 'IDENTIFIER' // Can use this to identify the type in the NavigationModal
},

 * to any of the Route objects underneath.
 * The modal and what you pass to it is entirely customizable.
 *
 */
export const routeMap: RoutesMap = {
  ROOT: {
    path: '/connect',
    thunk: (dispatch: Dispatch) => redirectToPage(dispatch)(Page.Home),
  },
  [Page.Home]: `${BASE_PATH}/home`,
  [Page.Cart]: `${BASE_PATH}/cart`,
  [Page.OrderConfirmation]: `${BASE_PATH}/cart/:id`,
  [Page.Login]: `${BASE_PATH}/login`,
  [Page.OrderDetails]: {
    path: `${BASE_PATH}/orders/(order)?/:id`,
    confirmLeave: (state: object) => {
      // The typing for redux-first-router is a bit funky, so need to do this unfortunately
      const navState = NavigationSelector.navState(Page.OrderDetails)(
        state as SharedState,
      )
      if (navState?.isDirty) {
        return ({
          type: NavigationBlockModalType.OrderEdit,
          message: 'message.changeswillbelost',
        } as unknown) as Nullable<string>
        // this cast is due to the types for the return of this fucntion in the router lib
      }
    },
  },
  [Page.Orders]: `${BASE_PATH}/orders`,
  [Page.QuoteDetails]: `${BASE_PATH}/quotes/(quote)?/:id`,
  [Page.Quotes]: `${BASE_PATH}/quotes`,
  [Page.ProductDetails]: `${BASE_PATH}/products/(product)?/:id`,
  [Page.Products]: `${BASE_PATH}/products`,
  [Page.Invoices]: `${BASE_PATH}/invoices`,
  [Page.InvoiceDetails]: `${BASE_PATH}/invoices/(invoice)?/:id`,
  [Page.Profile]: `${BASE_PATH}/profile`,
  [Page.UserEdit]: {
    path: `${BASE_PATH}/users/:userId`,
    confirmLeave: (state: object) => {
      // The typing for redux-first-router is a bit funky, so need to do this unfortunately
      const navState = NavigationSelector.navState(Page.UserEdit)(
        state as SharedState,
      )

      if (navState) {
        const { editType, isDirty, userType } = navState

        if (isDirty) {
          return ({
            type: NavigationBlockModalType.UserEdit,
            message: `userEdit.cancel.${editType}.${userType}`,
          } as unknown) as Nullable<string>
          // this cast is due to the types for the return of this fucntion in the router lib
        }
      }
    },
  },
  [Page.Users]: `${BASE_PATH}/users`,
  [Page.PaymentInvoice]: `${BASE_PATH}/payment-invoice/:id`,
  [Page.TermsAndConditions]: `${BASE_PATH}/terms/:typeOfTerms*`,
  [Page.PaymentInvoiceRedirect]: `${BASE_PATH}/payment-redirect-invoice/:documentId`,
  [Page.AccessRestricted]: `${BASE_PATH}/access-restricted`,
  [Page.NotFound]: `${BASE_PATH}/:url?`,
  [Page.NOT_FOUND]: {
    thunk: (dispatch: Dispatch) => redirectToNotFound(dispatch),
  },
}

export const routesToFlagsMap: RoutesMap = {
  [Page.Quotes]: Flag.QuotesPagePerCountry,
  [Page.QuoteDetails]: Flag.QuotesPagePerCountry,
  [Page.Invoices]: Flag.InvoicesPagePerCountry,
  [Page.InvoiceDetails]: Flag.InvoicesPagePerCountry,
  [Page.Users]: Flag.ExternalUserAdminPerCountry,
  [Page.UserEdit]: Flag.ExternalUserAdminPerCountry,
}

const isPage = (s: string) =>
  Object.values(Page).includes(s as Page) && Object.keys(routeMap).includes(s)

export const asPage = (s: string): Page => {
  if (isPage(s)) {
    return s as Page
  } else {
    throw `${s} is not a route`
  }
}
